import { CalendarDays, ChefHat, Clock3, Leaf } from "lucide-react"

import type { Recipe } from "@/types/recipe"
import { formatDate, formatMinutes } from "@/lib/utils"

type RecipeHeroProps = {
  recipe: Recipe
}

const difficultyMap: Record<Recipe["difficulty"], string> = {
  easy: "Легко",
  medium: "Середньо",
  hard: "Складніше",
}

const categoryMap: Record<Recipe["category"], string> = {
  cucumbers: "Огірки",
  tomatoes: "Помідори",
  zucchini: "Кабачки",
  peppers: "Перець",
  eggplants: "Баклажани",
  berries: "Ягоди",
  jam: "Варення",
  compote: "Компоти",
  fermentation: "Ферментація",
  other: "Інше",
}

const seasonMap: Record<string, string> = {
  spring: "Весна",
  summer: "Літо",
  autumn: "Осінь",
  winter: "Зима",
}

function getRecipeEmoji(category: Recipe["category"]) {
  switch (category) {
    case "cucumbers":
      return "🥒"
    case "tomatoes":
      return "🍅"
    case "zucchini":
      return "🥒"
    case "peppers":
      return "🌶️"
    case "eggplants":
      return "🍆"
    case "berries":
      return "🫐"
    case "jam":
      return "🍓"
    case "compote":
      return "🍎"
    case "fermentation":
      return "🥬"
    default:
      return "🍯"
  }
}

function formatServings(servings: number) {
  if (servings === 1) {
    return "1 банка"
  }

  if (servings >= 2 && servings <= 4) {
    return `${servings} банки`
  }

  return `${servings} банок`
}

function formatSeasonLabel(seasons: string[]) {
  if (seasons.length === 0) {
    return null
  }

  return seasons
    .map((season) => seasonMap[season] ?? season)
    .join(" • ")
}

export function RecipeHero({ recipe }: RecipeHeroProps) {
  const seasonLabel = formatSeasonLabel(recipe.season)

  return (
    <section className="overflow-hidden rounded-[2rem] border border-neutral-200 bg-white shadow-sm">
      <div className="grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="p-6 sm:p-8 lg:p-10">
          <div className="mb-4 flex flex-wrap gap-2">
            <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
              {categoryMap[recipe.category]}
            </span>

            <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
              {difficultyMap[recipe.difficulty]}
            </span>

            {recipe.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="inline-flex rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs font-medium text-neutral-700"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-3xl font-semibold leading-tight text-neutral-950 sm:text-4xl lg:text-5xl">
            {recipe.title}
          </h1>

          <p className="mt-5 max-w-3xl text-base leading-8 text-neutral-700 sm:text-lg">
            {recipe.description}
          </p>

          {seasonLabel ? (
            <div className="mt-5 inline-flex items-center rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-medium text-neutral-700">
              <Leaf className="mr-2 h-3.5 w-3.5 text-emerald-700" />
              Найдоречніше в сезон: {seasonLabel}
            </div>
          ) : null}

          <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-[1.25rem] border border-neutral-200 bg-neutral-50 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-950">
                <Clock3 className="h-4 w-4 text-emerald-700" />
                Час
              </div>
              <div className="text-sm text-neutral-600">
                {formatMinutes(recipe.totalTimeMinutes)}
              </div>
            </div>

            <div className="rounded-[1.25rem] border border-neutral-200 bg-neutral-50 p-4">
              <div className="mb-2 text-sm font-semibold text-neutral-950">
                Вихід
              </div>
              <div className="text-sm text-neutral-600">
                {formatServings(recipe.servings)}
              </div>
            </div>

            <div className="rounded-[1.25rem] border border-neutral-200 bg-neutral-50 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-950">
                <ChefHat className="h-4 w-4 text-emerald-700" />
                Складність
              </div>
              <div className="text-sm text-neutral-600">
                {difficultyMap[recipe.difficulty]}
              </div>
            </div>

            <div className="rounded-[1.25rem] border border-neutral-200 bg-neutral-50 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-neutral-950">
                <CalendarDays className="h-4 w-4 text-emerald-700" />
                Оновлено
              </div>
              <div className="text-sm text-neutral-600">
                {formatDate(recipe.updatedAt ?? recipe.publishedAt)}
              </div>
            </div>
          </div>
        </div>

        <div className="relative min-h-[280px] border-t border-neutral-200 bg-gradient-to-br from-emerald-100 via-amber-50 to-white lg:min-h-full lg:border-l lg:border-t-0">
          <div className="absolute inset-0 flex items-center justify-center text-[7rem] sm:text-[8rem] lg:text-[10rem]">
            {getRecipeEmoji(recipe.category)}
          </div>

          <div className="absolute left-6 top-6 rounded-full border border-white/80 bg-white/85 px-3 py-1 text-xs font-semibold text-neutral-800 backdrop-blur">
            Рецепт Кухлика
          </div>

          <div className="absolute bottom-6 left-6 right-6 rounded-[1.5rem] border border-white/80 bg-white/85 p-4 backdrop-blur">
            <div className="text-sm font-semibold text-neutral-950">
              Коротко про рецепт
            </div>
            <p className="mt-2 text-sm leading-7 text-neutral-600">
              {recipe.excerpt}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}